import React, {
  createContext,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { Prompt, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { MonacoEditor } from 'components/monaco';
import { escapeRegExp, isEqual } from 'lodash';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/CloseRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import DescriptionIcon from '@mui/icons-material/DescriptionRounded';
import { withStyles, withTheme } from '@mui/styles';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import {
  findTokens,
  getActiveVklKeyword,
  measure,
  vklLanguageConfiguration,
  vklLanguageId,
  vklMonarchLanguageDefinition,
  vklTokenTypes as types,
  vklTypes
} from 'studio/pages/Editor/VklSyntaxHighlighter';
import Progress from 'studio/components/Progress';
import { useMessage } from 'studio/components/Message';
import { ConfirmationModal, useConfirmationModal } from 'studio/components/ConfirmationModal/ConfirmationModal';
import { useLoader } from 'components/loaders/FadeLoader';
import services from 'services';
import { trackEvent } from 'utils/eventTracking';
import styles, { synchronizeGlyphMarginClassNamePrefix } from 'studio/pages/Editor/Editor.styles';
import Sidebar from 'studio/pages/Editor/Sidebar/Sidebar';
import Checkbox from 'studio/components/Checkbox';
import { pluralize as plrl, usePrevious } from 'studio/helpers';
import CodeIcon from '@mui/icons-material/CodeRounded';
import { paths as studioPaths } from 'studio/App/Studio/Studio';
import Content from 'studio/components/Content/Content';
import $ from 'jquery';
import { getSynchronizableConcepts, syncPolicies } from 'studio/pages/Synchronization/Synchronization';
import { KgPermissions } from 'studio/constants';
import { EntitiesCodeLensProvider } from 'studio/pages/Editor/EntitiesCodeLensProvider';
import {
  useCreateKgMessage,
  getDistinguishableName,
  replaceText,
  SYNC_PASSAGE_TYPE_STRING,
  DELETE_PASSAGE_TYPE_STRING
} from 'studio/pages/Editor/EditorUtils';
import { fileTypes } from 'studio/pages/Editor/FileTypeConstants';
import EditorLeftPanel from 'studio/pages/Editor/EditorLeftPanel';
import EditorFileToolbar from 'studio/pages/Editor/EditorFileToolbar';
import CopilotEditVklDialog from './CopilotEditVklDialog';
import CompareFilesModal from './CompareFilesModal/CompareFilesModal';
import { KeyCode, KeyMod, Uri } from 'monaco-editor';
import { useEditorOpen } from 'studio/hooks/useEditorOpen';

// returns the path of the parent folder (with trailing slash) of the specified file path
const parentFolderPath = filePath => filePath.substring(0, filePath.lastIndexOf('/') + 1);

export const treeTypes = {
  concept: 'concept',
  file: 'file'
};

export const queryParameterNames = {
  file: 'file',
  concept: 'concept'
};

/** Context available throughout the editor components to interact with the state */
export const EditorContext = createContext({});

let monaco = null;
let editor = null;
let codeLensProvider = null;

const editorInstances = {};

const comparisonDialogInitialState = {
  open: false,
  filePath: '',
  currentFile: null
};

const FilesDeletionConfirmationModal = withTheme(
  withStyles(styles)(
    ({
      classes,
      allFilesToDelete,
      closeFiles,
      deleteFiles,
      setFilesToDelete,
      setDeleteEntitiesFiles,
      entitiesFilesToDelete,
      deleteEntitiesFiles
    }) => {
      const existentFiles = allFilesToDelete.filter(file => file?.data?.path);

      const handleConfirm = () => {
        closeFiles(...existentFiles.map(file => file.data.path));
        return deleteFiles(existentFiles).then(() => {
          setFilesToDelete([]);
          setDeleteEntitiesFiles(true);
        });
      };

      const handleClose = () => {
        setFilesToDelete([]);
        setDeleteEntitiesFiles(true);
      };

      return (
        <ConfirmationModal
          open={existentFiles.length > 0}
          title={`Delete ${plrl(existentFiles, 'file')}`}
          onConfirm={handleConfirm}
          onClose={handleClose}
          confirmButtonText="Delete"
          confirmDataTest="editorConfirmDeleteFileButton"
        >
          {entitiesFilesToDelete.length > 0 && (
            <div className={classes.deleteEntitiesCheckbox}>
              <Checkbox
                color="primary"
                value={deleteEntitiesFiles}
                onChange={setDeleteEntitiesFiles}
                label={`Also delete entities ${plrl(entitiesFilesToDelete, 'file')}`}
              />
            </div>
          )}
          <div>
            The following {`${plrl(existentFiles, 'file')}`} will be deleted:
            {existentFiles.length > 0 && (
              <ul className={classes.modalFilePathListElement}>
                {existentFiles
                  .filter(file => file?.data?.path)
                  .sort((file1, file2) => file1.data.path.localeCompare(file2.data.path))
                  .map(file => {
                    return <li key={file.data.path}>{file.data.path}</li>;
                  })}
              </ul>
            )}
            Are you sure you want to delete {plrl(existentFiles, 'this file', 'these files')}?
          </div>
        </ConfirmationModal>
      );
    }
  )
);

const Editor = forwardRef(
  (
    { classes, theme, knowledgeGraph, fetchKnowledgeGraphs, menuContainerRef, user, setIsCommandPaletteOpen, ...props },
    ref
  ) => {
    const { withLoader } = useLoader();
    const { successMessage, errorMessage, warningMessage, closeSnackbar } = useMessage();

    const history = useHistory();
    const location = useLocation();
    const { showConfirmationModal } = useConfirmationModal();
    const openInEditor = useEditorOpen();

    // the files, indexed by file path
    const [files, setFiles] = useState(null);
    // the file contents, indexed by file path
    const [fileContents, setFileContents] = useState({});
    // contains information about the file to open
    const [fileInfoToOpen, setFileInfoToOpen] = useState(null);
    // the paths of the open files
    const [openFilePaths, setOpenFilePaths] = useState([]);
    // the path of the active (and open) file
    const [activeFilePath, setActiveFilePath] = useState(null);
    // the fully qualified VKL identifier of the concept in the active file (to jump to)
    const [activeVklIdentifier, setActiveVklIdentifier] = useState(null);
    // the definition of the concept to select in the concept tree, 'undefined' signalling it is still being determined
    const [selectedConceptTreeDefinition, setSelectedConceptTreeDefinition] = useState(undefined);
    // the files to delete
    const [filesToDelete, setFilesToDelete] = useState([]);
    // specifies whether to delete entities file when deleting a file containing a class with entities
    const [deleteEntitiesFiles, setDeleteEntitiesFiles] = useState(true);
    // the synchronizable concepts that can be selected for synchronization in the sync dialog
    const [syncDialogSynchronizableConcepts, setSyncDialogSynchronizableConcepts] = useState([]);
    // the synchronizable concepts of the currently active file in the editor
    const [activeFileSynchronizableConcepts, setActiveFileSynchronizableConcepts] = useState([]);
    // the synchronizable concepts that have been selected for synchronization in the sync dialog
    const [selectedSyncDialogSynchronizableConcepts, setSelectedSyncDialogSynchronizableConcepts] = useState(undefined);
    // whether we should show the sync dialog
    const [showSyncDialog, setShowSyncDialog] = useState(false);
    // contains the context for the copilot dialog to edit VKL
    const [copilotEditVklDialogContext, setCopilotEditVklDialogContext] = useState({});
    // whether we should show the comparison dialog
    const [comparisonDialog, setComparisonDialog] = useState(comparisonDialogInitialState);
    // the active VKL keyword, indexed by file, given the file's current cursor position
    const [activeVklKeywords, setActiveVklKeywords] = useState({});

    // the reference to the concept tree component
    const conceptTreeRef = useRef(null);

    /**
     * Reloads the concept and file trees.
     */
    const reloadTrees = async () => {
      // reload the files and wait for the files to have loaded s.t. they are up to date before potentially loading new
      // concepts in the concept tree (if the file of a concept is not loaded yet, clicking on it won't do anything)
      await getFiles();
      // reload all items in the concept tree
      conceptTreeRef.current.reloadTreeItems();
    };

    // exposes functions by making them available on the component's reference
    useImperativeHandle(ref, () => ({
      reloadTrees
    }));

    const kgIdRef = useRef(null);

    // extract some basic info from the knowledge graph
    const kgId = useMemo(() => knowledgeGraph?.data.id, [knowledgeGraph]);
    const kgPermissions = useMemo(() => knowledgeGraph?.data.kgPermissions || [], [knowledgeGraph]);
    // the active VKL keyword for the active file, given the file's current cursor position
    const activeVklKeyword = useMemo(() => activeVklKeywords[activeFilePath], [activeVklKeywords, activeFilePath]);
    // the active file currently being rendered and edited in the editor
    const activeFile = useMemo(() => files?.[activeFilePath], [files, activeFilePath]);
    // the active file content currently being rendered and edited in the editor
    const activeFileContent = useMemo(() => fileContents?.[activeFilePath], [fileContents, activeFilePath]);

    const tabValue = useMemo(() => {
      const currentValue = openFilePaths.indexOf(activeFilePath);
      return currentValue >= 0 ? currentValue : false;
    }, [openFilePaths, activeFilePath]);

    const hasModifiedFile = useMemo(
      () => openFilePaths.some(path => fileContents[path]?.isModified),
      [openFilePaths, fileContents]
    );

    const autocomplete = useCallback(
      monaco => async (model, position) => {
        const lineText = model.getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: 0,
          endLineNumber: position.lineNumber,
          endColumn: position.column
        });
        const { 0: word } = lineText.match(/[\w.]*$/);

        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: position.column - word.length,
          endColumn: position.column
        };
        const response = await services.autocompleteEditor(kgIdRef.current, word);
        if (response.success) {
          return {
            incomplete: true,
            suggestions: response.data.suggestions.map((suggestion, idx) => {
              const documentation = suggestion.documentation?.data;
              const documentationLink =
                documentation?.url &&
                `[Go to documentation](${documentation.url} "Open the documentation in a new window")`;
              return {
                label: suggestion.label,
                kind: monaco.languages.CompletionItemKind.Function, // This is more or less arbitrary, determines the icon displayed for the suggestion
                insertText: suggestion.completion,
                range,
                command: suggestion.completion.endsWith('.') // Trigger auto-complete suggestions if a suggestion ending in a dot is selected
                  ? {
                      id: 'editor.action.triggerSuggest',
                      title: 'Trigger next auto-complete pop-up'
                    }
                  : undefined,
                // Avoid any suggestions getting filtered out by Monaco editor due to the label not matching the word
                // Monaco does some post-processing of the suggestions here and filters all suggestions whose text does not match what we're searching
                // Needed for e.g. "client" returning "kb.Customer", due to labels on the resource
                filterText: word,
                documentation: documentation && {
                  value: `
# ${suggestion.isSnippet ? suggestion.label : suggestion.completion}
${documentation.markdown || ''}

${documentationLink || ''}
`
                },
                insertTextRules: suggestion.isSnippet
                  ? monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet
                  : monaco.languages.CompletionItemInsertTextRule.None,
                detail:
                  suggestion.completion !== suggestion.label && !suggestion.isSnippet
                    ? suggestion.completion
                    : undefined,
                // Ensure we keep the ordering given to us by the backend
                // Monaco sorts suggestions alphabetically, and we want to work around that
                sortText: idx.toString().padStart(6, '0') // Sort by padded string of the index
              };
            })
          };
        }
      },
      []
    );

    // retrieves the files
    const getFiles = useCallback(async () => {
      if (kgId) {
        const response = await withLoader(services.getKnowledgeGraphFiles(kgId));
        if (response.success) {
          // the files indexed by file path
          const files = response.data.data.reduce((obj, file) => ({ ...obj, [file.data.path]: file }), {});
          setFiles(files);
          return files;
        } else {
          const message = response.response?.data?.data?.message || 'There was an error while fetching file tree.';
          errorMessage(message);
        }
      }
    }, [kgId, errorMessage, withLoader]);

    // closes the open files with the specified paths
    const closeFiles = useCallback(
      (...paths) => {
        // remove the paths from the open file paths
        const newOpenFilePaths = openFilePaths.filter(path => !paths.includes(path));
        setOpenFilePaths(newOpenFilePaths);
        // dispose the files' editor models and remove their editor instances
        paths.forEach(path => {
          if (editorInstances[path]) {
            editorInstances[path].model.dispose();
            delete editorInstances[path];
          }
        });
        // remove the files' contents and active VKL keywords
        const mask = paths.reduce((mask, path) => ({ ...mask, [path]: undefined }), {});
        setFileContents(contents => ({ ...contents, ...mask }));
        setActiveVklKeywords({ ...activeVklKeywords, ...mask });
        // check if the active file path is being closed
        if (paths.includes(activeFilePath)) {
          // the index of the active file among the open files
          const index = openFilePaths.indexOf(activeFilePath);
          // the path of the new open file at the same index, falling back to the last new open file
          const newActiveFilePath = newOpenFilePaths[index] || newOpenFilePaths[newOpenFilePaths.length - 1];
          // if there is a new file to make active...
          if (newActiveFilePath) {
            // ...open it
            openInEditor(newActiveFilePath);
          } else {
            // ...else unset it
            setActiveFilePath(null);
            // ...and remove the file & concept param (if any)
            const params = new URLSearchParams(location.search);
            params.delete(queryParameterNames.file);
            params.delete(queryParameterNames.concept);
            history.push({ ...location, search: params.toString() });
          }
        }
      },
      [openFilePaths, activeFilePath, openInEditor, activeVklKeywords, location, history]
    );

    // retrieves the specified file's content
    const getFileContent = useCallback(
      async file => {
        if (kgId) {
          const response = await services.getKnowledgeGraphFileContent(kgId, file.data.id);
          if (response.success) {
            setFileContents(contents => ({ ...contents, [file.data.path]: response.data }));
          } else {
            const message = response.response?.data?.data?.message || 'There was an error getting file content.';
            errorMessage(message);
          }
        }
      },
      [kgId, closeFiles, errorMessage]
    );

    // opens the file with the specified path
    const openFile = useCallback(
      (path, vklQualifiedIdentifier) => {
        const file = files[path];
        if (file && path !== activeFilePath) {
          trackEvent('File Opened', {
            knowledgeGraph: { id: kgId },
            file: { id: file.data.id, path: file.data.path },
            vklQualifiedIdentifier
          });
          // add the file to the open file paths
          if (!openFilePaths.includes(path)) {
            setOpenFilePaths([...openFilePaths, path]);
          }
          // save the currently active file's editor view state
          if (editorInstances[activeFilePath]) {
            editorInstances[activeFilePath].viewState = editor.saveViewState();
          }
          // retrieve the file's content
          if (!fileContents[path]) {
            getFileContent(file);
          }
          setActiveFilePath(path);
        }
        // wrap the identifier in an object to trigger a state update also when the identifier is set again to the same value
        setActiveVklIdentifier({ value: vklQualifiedIdentifier });
      },
      [files, activeFilePath, openFilePaths, fileContents, getFileContent, kgId]
    );

    const makeManaged = useCallback(
      async file => {
        const response = await withLoader(services.makeKnowledgeGraphManaged(kgId, file.data.id));
        if (response.success) {
          successMessage('The file has successfully been added to Git');
          const newFile = { ...file };
          newFile.data.isUnmanaged = false;
          setFiles(files => ({ ...files, [file.data.path]: newFile }));
        } else {
          const message = response.response?.data || 'There was an error making the knowledge graph managed.';
          errorMessage(message);
        }
      },
      [kgId, withLoader, successMessage, errorMessage]
    );

    const getSynchronizableConceptsForFile = async (kgId, fileId) => {
      // retrieve all synchronizable concepts
      const synchronizableConcepts = await withLoader(getSynchronizableConcepts(kgId));
      // only keep the synchronizable concepts that are part of the specified file (ideally, at some
      // point the API would allow us to retrieve only the synchronizables concepts of a given file)
      return synchronizableConcepts.filter(
        concept => concept.data.file.id === fileId || concept.data.entitiesFile?.id === fileId
      );
    };

    /**
     * Opens the synchronization dialog.
     *
     * @param fileId the ID of the file to open the synchronization dialog for
     * @param classVklIdentifiers the VKL identifiers or fully qualified VKL identifiers of synchronizables to preselect
     */
    const openSyncDialog = async (fileId, classVklIdentifiers) => {
      // retrieve all synchronizable concepts for the file
      const fileSynchronizableConcepts = await withLoader(getSynchronizableConceptsForFile(kgId, fileId));
      const selectedSynchronizableConcepts = fileSynchronizableConcepts.filter(
        concept =>
          // pre-select the concept if it doesn't have an 'ignore' sync policy...
          concept.data.syncPolicy !== syncPolicies.ignore &&
          // ...and it matches one of the specified fully qualified VKL identifers
          (classVklIdentifiers?.includes(concept.data.vklQualifiedIdentifier) ||
            // ...or it matches one of the specified VKL identifers
            classVklIdentifiers?.includes(concept.data.vklIdentifier) ||
            // ...or if no identifiers have been specified
            !classVklIdentifiers)
      );
      setSyncDialogSynchronizableConcepts(fileSynchronizableConcepts);
      setSelectedSyncDialogSynchronizableConcepts(selectedSynchronizableConcepts);
      setShowSyncDialog(true);
    };

    // sets the context to open the copilot dialog to edit VKL
    const openCopilotEditVklDialog = async () => {
      // use the whole file by default...
      let vkl = editor.getModel().getValue();
      let isSelection = false;

      // ...unless there is a selection
      const selectedText = editor.getModel().getValueInRange(editor.getSelection());
      if (selectedText) {
        vkl = selectedText;
        isSelection = true;
      }

      setCopilotEditVklDialogContext({ isOpen: true, vkl, file: activeFilePath, isSelection });
    };

    // unsets the context to close the copilot dialog to edit VKL
    const closeCopilotEditVklDialog = () => setCopilotEditVklDialogContext({});

    const openComparisonDialog = () =>
      setComparisonDialog({
        open: true,
        filePath: activeFilePath,
        currentFile: fileContents[activeFilePath]
      });

    const closeComparisonDialog = () => setComparisonDialog(comparisonDialogInitialState);

    const createKgMessage = useCreateKgMessage(
      classes,
      setFilesToDelete,
      openSyncDialog,
      openInEditor,
      getFiles,
      activeFile
    );

    const saveFile = useCallback(
      async (file, fileContent, content) => {
        const headers = { 'Content-Type': `${fileContent.contentType}; charset=UTF-8` };
        const response = await withLoader(services.saveKnowledgeGraphFileContent(kgId, file.data.id, content, headers));
        if (response.success) {
          const warningMessages = response.data.warning?.data?.messages;
          if (warningMessages) {
            warningMessages.forEach(warning => {
              const callback = passage => {
                if (passage.type === SYNC_PASSAGE_TYPE_STRING || passage.type === DELETE_PASSAGE_TYPE_STRING) {
                  closeSnackbar(snackbarKey);
                }
              };
              const [passages] = createKgMessage(warning, callback);
              const snackbarKey = warningMessage(
                <span style={{ whiteSpace: 'pre-wrap' }}>{passages}</span>,
                true,
                true
              );
            });
          } else {
            successMessage('File saved successfully!');
          }
          trackEvent('File Saved', { file: { id: file.data.id, path: file.data.path } });
          const newFileContent = {
            ...(response.data.content ? { ...response.data.content } : {}),
            // use the current editor content instead of the one returned by the backend (which is potentially reorderd)
            // since this content is compared to the editor content in 'setIsModified' to determine the file modification
            // status
            content,
            // unset the definition's offsets as they are potentially invalid (due to the potential reordering of the
            // content by the backend), s.t. the jumping to a concept in the editor falls back to the regex approach
            ...(response.data.content?.definitions
              ? { definitions: response.data.content.definitions?.map(definition => ({ ...definition, offset: null })) }
              : {})
          };
          setFileContents(contents => ({ ...contents, [file.data.path]: newFileContent }));
          // update the concept tree
          conceptTreeRef.current.reloadTreeItems([file]);
        } else {
          const message = response.response?.data?.data?.message || 'There was an error while saving the file';
          errorMessage(message);
        }
      },
      [kgId, withLoader, successMessage, errorMessage, files, openSyncDialog]
    );

    const saveFileContent = useCallback(
      async (_, skipUnmanagedCheck = false) => {
        const content = editor.getValue();
        if (activeFile.data.isUnmanaged && !skipUnmanagedCheck) {
          showConfirmationModal(() => ({
            title: 'Add to Git',
            content: `Do you want to add this file to Git? This is necessary to save the pending changes.`,
            confirmButtonText: 'Add file to git and save',
            onConfirm: async () => {
              await makeManaged(activeFile);
              await saveFileContent(null, true);
            }
          }));
        } else {
          saveFile(activeFile, activeFileContent, content);
        }
      },
      [activeFile, activeFileContent, makeManaged, saveFile]
    );

    const verifyChangesAndSaveFileContent = useCallback(() => {
      if (activeFileContent.isModified) {
        saveFileContent();
      }
    }, [activeFileContent, saveFileContent]);

    // deletes the specified files and afterwards retrieves all files anew
    const deleteFiles = useCallback(
      files => {
        const promises = files.map(file => services.deleteKnowledgeGraphFile(kgId, file.data.id));
        return Promise.all(promises)
          .then(() => {
            trackEvent(`Editor ${plrl(files, 'File')} Deleted`, { files: files.map(file => ({ id: file.data.id })) });
            successMessage(`The ${plrl(files, 'file has', 'files have')} successfully been deleted`);
            // update the concept tree
            conceptTreeRef.current.reloadTreeItems(files);
          })
          .catch(() => errorMessage(`There was an error while deleting the ${plrl(files, 'file')}`))
          .finally(() => getFiles());
      },
      [closeFiles, kgId, getFiles, successMessage, errorMessage]
    );

    // set the synchronizable concepts, whenever we change kg, the active file or create/delete/save a file
    useEffect(() => {
      (async () => {
        const fileId = activeFile?.data?.id;
        if (fileId) {
          const fileSynchronizableConcepts = await getSynchronizableConceptsForFile(kgId, fileId);
          // only update the synchronizable concepts if they have changed (deep object comparison)
          // otherwise we would trigger a flicker re-render of the code lens e.g. when we make the active file modified
          if (!isEqual(fileSynchronizableConcepts, activeFileSynchronizableConcepts)) {
            setActiveFileSynchronizableConcepts(fileSynchronizableConcepts);
          }
        }
      })();
    }, [kgId, activeFile, files, activeFileContent?.isModified]);

    // registers / updates the click listener for synchronize decorations in the editor margin
    useEffect(() => {
      const selector = `*[class*='${synchronizeGlyphMarginClassNamePrefix}']`;
      // remove the previous listener
      $(document).off('click', selector);
      // add a new listener with the up-to-date versions of the active file and 'openSyncDialog' method
      $(document).on('click', selector, event => {
        // the CSS class names of the element that was clicked
        const classNames = $(event.target).attr('class').split(/\s+/);
        // the name of the glyph margin CSS class of the synchronize decoration
        const className = classNames.find(className => className.startsWith(synchronizeGlyphMarginClassNamePrefix));
        // extract the VKL identifier of the class from the CSS class name
        const vklIdentifier = className?.slice(synchronizeGlyphMarginClassNamePrefix.length);
        // open the synchronization dialog, pre-selecting the class with the identifier
        openSyncDialog(activeFile.data.id, [vklIdentifier]);
      });
    }, [openSyncDialog, activeFile]);

    // reloads the contents of the files with the specified paths
    const reloadFileContents = useCallback(
      (...paths) => {
        const promises = paths.map(async path => {
          const file = files[path];
          const response = await services.getKnowledgeGraphFileContent(kgId, file.data.id);
          const fileContent = response.data;
          setFileContents(fileContents => ({ ...fileContents, [path]: fileContent }));
          const model = editorInstances[path]?.model;
          model && replaceText(model, fileContent.content);
        });
        return Promise.all(promises);
      },
      [files, kgId]
    );

    const setTheme = useCallback(() => {
      if (monaco) {
        monaco.editor.defineTheme(theme.palette.mode, {
          base: 'vs' + (theme.isDark ? '-dark' : ''),
          inherit: true,
          ...theme.editor
        });
        monaco.editor.setTheme(theme.palette.mode);
      }
    }, [theme]);

    // finds the entities files of the specified (class) file
    const findEntitiesFiles = useCallback(
      file => {
        if (file.data.type === fileTypes.class) {
          const classFolderPath = parentFolderPath(file.data.path);
          const filesInFolder = Object.keys(files)
            .map(path => files[path])
            .filter(file => parentFolderPath(file.data.path).startsWith(classFolderPath));
          return filesInFolder.filter(file => file.data.type === fileTypes.entities);
        } else {
          return [];
        }
      },
      [files]
    );

    const setIsModified = useCallback(
      content => {
        const isModified = content !== activeFileContent.content;
        if (isModified !== activeFileContent.isModified) {
          setFileContents({
            ...fileContents,
            [activeFilePath]: {
              ...activeFileContent,
              isModified
            }
          });
        }
      },
      [fileContents, activeFileContent, activeFilePath]
    );

    const editorWillMount = useCallback(
      monaco => {
        monaco.languages.register({ id: vklLanguageId });
        monaco.languages.setLanguageConfiguration(vklLanguageId, vklLanguageConfiguration);
        // set our own syntax highlighting for VKL
        monaco.languages.setMonarchTokensProvider(vklLanguageId, vklMonarchLanguageDefinition);

        monaco.languages.onLanguage(vklLanguageId, () => {
          // register auto-complete for VKL
          monaco.languages.registerCompletionItemProvider(vklLanguageId, {
            triggerCharacters: ['.'],
            provideCompletionItems: autocomplete(monaco)
          });
        });

        // On Ctrl/Cmd+K, open the definition and file search
        // This is actually a global keybinding, i.e. works also when the editor is not focused,
        // and the event listener for it is actually defined in Studio.js
        // However, Monaco already captures this keybinding and thus the global event listener doesn't work,
        // the easiest workaround is to add a command that opens the command palette
        monaco.editor.addCommand({
          id: 'open-definition-file-search',
          run: () => {
            setIsCommandPaletteOpen(true);
          }
        });
        monaco.editor.addKeybindingRule({
          keybinding: KeyMod.CtrlCmd | KeyCode.KeyK,
          command: 'open-definition-file-search'
        });
      },
      [autocomplete]
    );

    // moves the editor's cursor to the active VKL identifier
    useEffect(() => {
      if (activeFile && activeFileContent && activeVklIdentifier?.value) {
        const model = editorInstances[activeFile.data.path]?.model;
        if (model) {
          let position;
          // find the definition corresponding to the identifier in the file content
          const definition = activeFileContent.definitions?.find(
            definition => definition.vklQualifiedIdentifier === activeVklIdentifier.value
          );
          if (definition) {
            // if the definition has an offset and the file content is not modified...
            if (parseInt(definition.offset) && !activeFileContent.isModified) {
              // ...jump to the definition's offset
              position = model.getPositionAt(definition.offset);
            } else {
              // ...else try to find it using a regex
              // the regular expression for the definition's block, i.e. 'number Amount {'
              const regex = `${escapeRegExp(definition.vklType)}[\\s]+${escapeRegExp(
                definition.vklIdentifier
              )}[\\s]*\\{`;
              const matches = model.findMatches(new RegExp(regex), false, true, true, true);
              const matchRange = matches[0]?.range;
              position = matchRange && { lineNumber: matchRange.startLineNumber, column: matchRange.startColumn };
            }
          }
          if (position) {
            editor.setPosition(position);
            editor.revealLineInCenter(position.lineNumber);
          }
          editor.focus();
        }
        setActiveVklIdentifier(null);
      }
    }, [activeFile, activeFileContent, activeVklIdentifier]);

    useEffect(() => {
      // check first if the code lens provider has already been registered
      if (codeLensProvider) {
        codeLensProvider.dispose();
        codeLensProvider = null;
      }
      if (editor && activeFile) {
        const openEntitiesCommand = editor.addCommand(0, (_, path) => openInEditor(path));
        const synchronizeCommand = editor.addCommand(0, (_, vklIdentifier) =>
          openSyncDialog(activeFile.data.id, [vklIdentifier])
        );
        const entitiesCodeLensProvider = new EntitiesCodeLensProvider(
          monaco,
          activeFileSynchronizableConcepts,
          openEntitiesCommand,
          synchronizeCommand
        );
        codeLensProvider = monaco.languages.registerCodeLensProvider(vklLanguageId, entitiesCodeLensProvider);
      }
    }, [editor, activeFile, activeFileSynchronizableConcepts, openInEditor, openSyncDialog, kgId]);

    const editorDidMount = useCallback(
      (newEditor, newMonaco) => {
        monaco = newMonaco;
        editor = newEditor;

        let model = null;
        if (editorInstances[activeFilePath]) {
          model = editorInstances[activeFilePath].model;
          editor.setModel(model);
          editor.restoreViewState(editorInstances[activeFilePath].viewState);
        } else {
          const uri = Uri.parse('file:///' + activeFilePath);
          model = monaco.editor.createModel(activeFileContent.content, vklLanguageId, uri);
          editorInstances[activeFilePath] = { model };
          editor.setModel(model);
          // creates / updates the synchronize buttons for classes in the editor margin
          // (cf. https://github.com/microsoft/vscode/blob/1a55beb2aaee64eb8d0cbe5b61b10088bbc6c895/src/vs/editor/common/textModelEvents.ts#L94-L107)
          model.onDidChangeTokens(event =>
            measure(false, () => {
              // the numbers of the lines of the ranges where tokenization has changed
              const lineNumbers = event.ranges.flatMap(({ fromLineNumber, toLineNumber }) =>
                Array(toLineNumber - fromLineNumber + 1)
                  .fill()
                  .map((_, idx) => fromLineNumber + idx)
              );
              // the current decorations in the ranges where tokenization has changed
              const oldDecorations = lineNumbers.flatMap(lineNumber =>
                // get the decorations for the current line
                model
                  .getLineDecorations(lineNumber)
                  // only keep if its glyph class name matches
                  .filter(decoration =>
                    decoration.options.glyphMarginClassName?.startsWith(synchronizeGlyphMarginClassNamePrefix)
                  )
              );
              // the new decorations in the ranges where tokenization has changed
              const newDecorations = lineNumbers.flatMap(lineNumber => {
                // start looking for tokens at the beginning of the line
                const startPosition = new monaco.Position(lineNumber, 1);
                // forward-match tokens |class|, |Order|, |{| in example 'class Order |{'
                const tokens = findTokens(
                  monaco,
                  model,
                  startPosition,
                  [types.keyword, types.identifier, types.curlyBracket],
                  [],
                  true
                );
                // check that the keyword token is a 'class' and that is on the current line
                if (tokens?.[0].value === vklTypes.class && tokens[0].position.lineNumber === lineNumber) {
                  // create the decoration
                  return [
                    {
                      range: new monaco.Range(lineNumber, 1, lineNumber, 1),
                      options: {
                        // append the VKL identifier of the class to the CSS class name prefix (ideally we would use the
                        // fully qualified identifier but don't have it available given the limitation of Monaco's parser)
                        glyphMarginClassName: synchronizeGlyphMarginClassNamePrefix + tokens[1].value,
                        glyphMarginHoverMessage: {
                          value: 'Synchronize entities'
                        }
                      }
                    }
                  ];
                } else {
                  return [];
                }
              });
              // remove the old and add the new decorations
              model.deltaDecorations(
                oldDecorations.map(decoration => decoration.id),
                newDecorations
              );
            })
          );
        }
        editor.focus();

        editor.updateOptions({ wordBasedSuggestions: false });

        /**
         * Gets called whenever the position of the cursor changes and updates the active VKL keyword.
         */
        editor.onDidChangeCursorPosition(event => {
          const activeVklKeyword = getActiveVklKeyword(monaco, model, event.position);
          setActiveVklKeywords({ ...activeVklKeywords, [activeFilePath]: activeVklKeyword });
        });

        setTheme();
      },
      [activeFilePath, activeFileContent, setTheme, activeVklKeywords]
    );

    // The (initial) value to set for the 'MonacoEditor' component: it needs to match the value of the model that we
    // set or create in 'editorDidMount' above, so we define it equivalently here. The reason we need to set the
    // (initial) value for 'MonacoEditor' is that the component initially sets the model value to the value passed to
    // it, cf. https://github.com/react-monaco-editor/react-monaco-editor/blob/d2fd2521e0557c880dec93acaab9a087f025426c/src/editor.tsx#L108-L131
    // So if we omit that value, then the component sets the model value to the empty string, leading to an empty
    // editor being shown.
    // Note: we should likely refactor this and not manually set or create models at all (cf. 'SaveDefinitionForm'
    // component).
    const initialEditorValue = editorInstances[activeFilePath]?.model.getValue() || activeFileContent?.content;

    // adds (updates) the editor's command for CTRL + S to save the active file
    // (this needs to be in a separate effect s.t. the command always uses the most recent version of 'verifyChangesAndSaveFileContent')
    useEffect(() => {
      editor?.addCommand([monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS], verifyChangesAndSaveFileContent);
      editor?.addCommand([monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyC], openCopilotEditVklDialog);
    }, [verifyChangesAndSaveFileContent]);

    useEffect(() => {
      if (knowledgeGraph) {
        setFiles(null);
        setFileContents({});
        setOpenFilePaths([]);
        setActiveFilePath(null);
        setDeleteEntitiesFiles(true);
        getFiles();
      }
    }, [knowledgeGraph, getFiles]);

    // We assign the KG ID to a ref and use it inside the "autocomplete" function.
    // This is necessary because the "autocomplete" is assigned to the editor instance only
    // on the first time it renders (editorWillMount), and it is not updated when the memo "kgId" changes,
    // even though we pass it as a dependency to the "autocomplete" function.
    useEffect(() => {
      if (kgId) {
        kgIdRef.current = kgId;
      }
    }, [kgId]);

    useEffect(() => {
      setTheme();
    }, [theme.palette.mode, setTheme]);

    // keep track of the previous location
    const previousLocation = usePrevious(location);

    // sets the file info to open when the location changes
    useEffect(() => {
      if (location !== previousLocation && !fileInfoToOpen?.path) {
        const params = new URLSearchParams(location.search);
        // split by the separator character (';')
        const file = params.get(queryParameterNames.file);
        const vklQualifiedIdentifier = params.get(queryParameterNames.concept);
        const path = file?.replace(/^\/|\/$/g, '');
        // on the first update, when the previous location is undefined, we specify to select the concept in the concept tree
        const selectInConceptTree = !previousLocation;
        // set it to null to signal that there is no selected definition
        !vklQualifiedIdentifier && setSelectedConceptTreeDefinition(null);
        setFileInfoToOpen({ path, vklQualifiedIdentifier, selectInConceptTree });
      }
    }, [location, !previousLocation, fileInfoToOpen]);

    // opens the file based on the file info to open once the files have been retrieved
    useEffect(() => {
      if (files && fileInfoToOpen?.path) {
        setFileInfoToOpen({ ...fileInfoToOpen, path: null });
        openFile(fileInfoToOpen.path, fileInfoToOpen.vklQualifiedIdentifier);
      }
    }, [!files, fileInfoToOpen, openFile]);

    // selects the concept from the file info to open in the concept tree once the active file's content is available
    useEffect(() => {
      if (activeFileContent && fileInfoToOpen?.selectInConceptTree) {
        setFileInfoToOpen(null);
        const definition = activeFileContent.definitions?.find(
          definition => definition.vklQualifiedIdentifier === fileInfoToOpen.vklQualifiedIdentifier
        );
        // set the selected definition or null to signal that there is none
        setSelectedConceptTreeDefinition(definition || null);
      }
    }, [activeFileContent, fileInfoToOpen]);

    useEffect(() => {
      window.onbeforeunload = hasModifiedFile ? () => true : (window.onbeforeunload = undefined);
    }, [hasModifiedFile]);

    // specifies whether the editor is currently shown, i.e. if the current route matches the editor path
    const isEditorRoute = !!useRouteMatch({ path: studioPaths.editor, exact: true });
    // specifies whether the editor was previously shown, i.e. if the previous route matched the editor path
    const wasEditorRoute = usePrevious(isEditorRoute);

    // closes all open files when leaving the editor
    useEffect(() => {
      // if we switch from the editor route to a non-editor route...
      if (wasEditorRoute && !isEditorRoute) {
        // ...then close all open files
        closeFiles(...openFilePaths);
      }
    }, [isEditorRoute, wasEditorRoute, openFilePaths, closeFiles]);

    const entitiesFilesToDelete = useMemo(
      () => (filesToDelete.length === 1 ? findEntitiesFiles(filesToDelete[0]) : []),
      [filesToDelete, findEntitiesFiles]
    );

    const allFilesToDelete = useMemo(
      () => [...filesToDelete, ...(deleteEntitiesFiles ? entitiesFilesToDelete : [])],
      [filesToDelete, deleteEntitiesFiles, entitiesFilesToDelete]
    );

    const editorContextValue = {
      knowledgeGraph,
      fetchKnowledgeGraphs,
      user,
      kgId,
      kgPermissions,
      editor,
      editorInstances,

      navigation: {
        openInEditor
      },
      fileState: {
        files,
        setFiles,
        openFilePaths,
        fileContents,
        activeFile,
        activeFilePath,
        activeFileContent
      },
      fileOperations: {
        getFiles,
        closeFiles,
        reloadFileContents,
        saveFileContent
      },
      refs: {
        menuContainerRef,
        conceptTreeRef
      },
      deletion: {
        filesToDelete,
        setFilesToDelete
      },
      sync: {
        showSyncDialog,
        openSyncDialog,
        selectedSyncDialogSynchronizableConcepts,
        setSelectedSyncDialogSynchronizableConcepts,
        setShowSyncDialog,
        syncDialogSynchronizableConcepts,
        activeFileSynchronizableConcepts
      },
      openCopilotEditVklDialog,
      openComparisonDialog,
      trees: {
        selectedConceptTreeDefinition
      }
    };

    return (
      <EditorContext.Provider value={editorContextValue}>
        <EditorLeftPanel />

        <Box className={`${classes.main}`} {...props}>
          {!activeFilePath && (
            <Content>
              <Typography variant="h1">
                <CodeIcon />
                Editor
              </Typography>
            </Content>
          )}
          {activeFilePath && (
            <div className={classes.editor}>
              <div className={classes.filebar}>
                <Tabs
                  value={tabValue}
                  onChange={(_, i) => openInEditor(openFilePaths[i])}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="scrollable"
                  scrollButtons
                >
                  {openFilePaths.map(path => (
                    <Tooltip placement="bottom" title={path} key={path} classes={{ tooltip: classes.fileTooltip }}>
                      <Tab
                        label={
                          <>
                            <DescriptionIcon />
                            <span>
                              {getDistinguishableName(path, [...openFilePaths, 'class.ttl']) +
                                (fileContents[path]?.isModified ? '*' : '')}
                            </span>
                            <CloseIcon
                              onClick={event => {
                                event.stopPropagation();
                                if (fileContents[path]?.isModified) {
                                  showConfirmationModal(() => ({
                                    title: 'Close file',
                                    content: `The file has unsaved changes. Do you want discard the changes and close the file?`,
                                    confirmButtonText: 'Discard changes',
                                    onConfirm: () => closeFiles(path)
                                  }));
                                } else {
                                  closeFiles(path);
                                }
                              }}
                            />
                          </>
                        }
                      />
                    </Tooltip>
                  ))}
                </Tabs>
              </div>
              {!activeFile || !activeFileContent ? (
                <Progress />
              ) : (
                <>
                  <EditorFileToolbar knowledgeGraph={knowledgeGraph} />
                  {activeFile.data.isUnmanaged && (
                    <Paper square height="30px" elevation={1} className={classes.unmanagedWarningBar}>
                      <span>
                        <IconButton size="large" className={classes.actionButton}>
                          <WarningRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                      This file is not in Git currently. In order to edit it, you will need to add it to Git.
                      <MuiLink
                        href="#"
                        style={{ marginLeft: 'auto' }}
                        color="primary"
                        className={classes.actionLink}
                        onClick={e => {
                          e.preventDefault();
                          makeManaged(activeFile);
                        }}
                        data-test="editorAddToGit"
                      >
                        Add to Git
                      </MuiLink>
                    </Paper>
                  )}
                  <div className={classes.editorContainer}>
                    <div className={classes.monacoContainer} data-test="monaco-editor-container">
                      <MonacoEditor
                        value={initialEditorValue}
                        language="vkl"
                        theme={theme.palette.mode}
                        options={{
                          scrollBeyondLastLine: false,
                          showUnused: false,
                          codeLens: true,
                          automaticLayout: true,
                          readOnly: !kgPermissions.includes(KgPermissions.Write),
                          scrollbar: {
                            verticalScrollbarSize: 6
                          },
                          // show the glyph margin for decorations
                          glyphMargin: true,
                          // the minimum number of characters to use for the line numbers
                          lineNumbersMinChars: 3
                        }}
                        key={activeFilePath}
                        overrideServices={{
                          // This is used so we can have the documentation open by default during autocomplete in the Editor
                          // Check https://github.com/microsoft/monaco-editor/issues/2241#issuecomment-764694521
                          storageService: {
                            get() {},
                            remove() {},
                            getBoolean(key) {
                              return true;
                            },
                            getNumber(key) {
                              return 0;
                            },
                            store() {},
                            onWillSaveState() {},
                            onDidChangeStorage() {}
                          }
                        }}
                        onChange={setIsModified}
                        editorDidMount={editorDidMount}
                        editorWillMount={editorWillMount}
                      />
                    </div>
                    <Sidebar
                      // manually dispatch a resize event s.t. Monaco correctly resizes whenever the sidebar resizes
                      onResize={() => window.dispatchEvent(new Event('resize'))}
                      activeKeyword={activeVklKeyword}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </Box>

        {/*Need to use ConfirmationModal manually here because we use state for the content */}
        <FilesDeletionConfirmationModal
          allFilesToDelete={allFilesToDelete}
          closeFiles={closeFiles}
          deleteFiles={deleteFiles}
          setFilesToDelete={setFilesToDelete}
          setDeleteEntitiesFiles={setDeleteEntitiesFiles}
          entitiesFilesToDelete={entitiesFilesToDelete}
          deleteEntitiesFiles={deleteEntitiesFiles}
        />
        <CopilotEditVklDialog
          isOpen={!!copilotEditVklDialogContext.isOpen}
          knowledgeGraph={knowledgeGraph}
          vkl={copilotEditVklDialogContext.vkl}
          file={copilotEditVklDialogContext.file}
          isSelection={copilotEditVklDialogContext.isSelection}
          setVkl={editedVkl => {
            const model = editorInstances[activeFilePath].model;
            // the range of the text to replace, if we are replacing a selection
            const range = copilotEditVklDialogContext.isSelection ? editor.getSelection() : null;
            replaceText(model, editedVkl, range);
            setTimeout(() => editor.focus(), 300);
          }}
          onClose={() => {
            closeCopilotEditVklDialog();
            setTimeout(() => editor.focus(), 300);
          }}
        />
        <CompareFilesModal
          knowledgeGraph={knowledgeGraph?.data}
          open={comparisonDialog.open}
          filePath={comparisonDialog.filePath}
          currentFileContent={comparisonDialog.currentFile?.content}
          onClose={closeComparisonDialog}
        />

        <Prompt
          message={newLocation => {
            if (hasModifiedFile) {
              // delete the 'file' and 'concept' query parameters s.t. we ignore them when comparing the search
              const params = new URLSearchParams(location.search);
              params.delete(queryParameterNames.file);
              params.delete(queryParameterNames.concept);
              const newParams = new URLSearchParams(newLocation.search);
              newParams.delete(queryParameterNames.file);
              newParams.delete(queryParameterNames.concept);
              // check if the path or (to be conservative) if any query parameter has changed
              if (newLocation.pathname !== location.pathname || newParams.toString() !== params.toString()) {
                return 'You have unsaved changes. Do you really want to leave the Editor?';
              }
            }
            return true;
          }}
        />
      </EditorContext.Provider>
    );
  }
);

export default withTheme(withStyles(styles)(Editor));
